'use client';

import { c as _c } from "react/compiler-runtime";
import cardValidator from 'card-validator';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isEncryptedCardMask } from '@sola/shared-utils/credit-cards.ts';
import { InputField, InputFieldProps } from '../input-field/input-field.tsx';
import { removeNonDigits, resetAchRoutingAndAccount, shouldRemoveSpecialChars, validationKeys } from './utils.ts';
type ACHNumberFieldProps = Omit<InputFieldProps, 'maskOptions' | 'placeholder' | 'prefix' | 'type' | 'minLength' | 'maxLength' | 'pattern' | 'aria-label' | 'aria-labelledby'> & {
  name: string;
  // Enables validation to prevent matching routing and account numbers
  routingNumberFieldName?: string;
};
export const ACHAccountNumber: React.FC<ACHNumberFieldProps> = t0 => {
  const $ = _c(48);
  let fieldProps;
  let routingNumberFieldName;
  if ($[0] !== t0) {
    ({
      routingNumberFieldName,
      ...fieldProps
    } = t0);
    $[0] = t0;
    $[1] = fieldProps;
    $[2] = routingNumberFieldName;
  } else {
    fieldProps = $[1];
    routingNumberFieldName = $[2];
  }
  const {
    name
  } = fieldProps;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["checkout", "payment-methods"];
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    t
  } = useTranslation(t1);
  const formContext = useFormContext();
  const {
    setValue,
    getValues,
    trigger,
    formState
  } = formContext;
  const t2 = routingNumberFieldName || "";
  const t3 = !routingNumberFieldName;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      name: t2,
      disabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const routingNumber = useWatch(t4);
  let t5;
  if ($[7] !== formContext) {
    t5 = () => resetAchRoutingAndAccount(formContext);
    $[7] = formContext;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const clearValues = t5;
  let t6;
  if ($[9] !== formState.errors || $[10] !== getValues || $[11] !== name || $[12] !== routingNumber || $[13] !== trigger) {
    t6 = () => {
      if (!routingNumber || formState.errors[name]?.type !== validationKeys.differentNumbers) {
        return;
      }
      const accountNumber = getValues(name);
      if (accountNumber !== routingNumber) {
        trigger(name);
      }
    };
    $[9] = formState.errors;
    $[10] = getValues;
    $[11] = name;
    $[12] = routingNumber;
    $[13] = trigger;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== formState.errors || $[16] !== routingNumber) {
    t7 = [routingNumber, formState.errors];
    $[15] = formState.errors;
    $[16] = routingNumber;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  if ($[18] !== t) {
    t8 = t("payment-methods:ach.fields.account");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t10;
  let t9;
  if ($[20] !== getValues || $[21] !== name || $[22] !== setValue) {
    t9 = () => {
      setValue(name, getValues(name).trim(), {
        shouldValidate: true
      });
    };
    t10 = () => {
      const newValue = getValues(name);
      if (shouldRemoveSpecialChars(newValue)) {
        setValue(name, removeNonDigits(newValue));
      }
    };
    $[20] = getValues;
    $[21] = name;
    $[22] = setValue;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  let t11;
  if ($[25] !== clearValues) {
    t11 = e => {
      if (isEncryptedCardMask(e.target.value)) {
        clearValues();
      }
    };
    $[25] = clearValues;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = value => {
      if (isEncryptedCardMask(value)) {
        return true;
      }
      return value.length >= 4 && value.length <= 17 || t("checkout:form.ach.accountInvalid");
    };
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== formContext || $[30] !== routingNumberFieldName || $[31] !== t) {
    t13 = value_0 => {
      if (isEncryptedCardMask(value_0)) {
        return true;
      }
      return !routingNumberFieldName || formContext.getValues()[routingNumberFieldName] !== value_0 || t("checkout:form.ach.accountRoutingMatch");
    };
    $[29] = formContext;
    $[30] = routingNumberFieldName;
    $[31] = t;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  let t15;
  if ($[33] !== t) {
    t14 = value_1 => {
      if (isEncryptedCardMask(value_1)) {
        return true;
      }
      return !cardValidator.number(value_1).isValid || t("checkout:form.ach.accountNumber");
    };
    t15 = value_2 => {
      if (isEncryptedCardMask(value_2)) {
        return true;
      }
      return /^(\d)+((-(\d)+)|( (\d)+)){0,}$/.test(value_2) || t("checkout:form.ach.accountInvalid");
    };
    $[33] = t;
    $[34] = t14;
    $[35] = t15;
  } else {
    t14 = $[34];
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t12 || $[37] !== t13 || $[38] !== t14 || $[39] !== t15) {
    t16 = {
      validLength: t12,
      [validationKeys.differentNumbers]: t13,
      validAccountNumber: t14,
      validAccountNumberFormat: t15
    };
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== fieldProps || $[42] !== t10 || $[43] !== t11 || $[44] !== t16 || $[45] !== t8 || $[46] !== t9) {
    t17 = <InputField autoComplete="off" label={t8} inputMode="numeric" onBlur={t9} onChange={t10} onFocus={t11} validate={t16} {...fieldProps} />;
    $[41] = fieldProps;
    $[42] = t10;
    $[43] = t11;
    $[44] = t16;
    $[45] = t8;
    $[46] = t9;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
};